import { useContext, useState, useEffect } from "react";
import ThemeContext from "../../lib/theme-context";
import moment from "moment";
import CustomSelect from "../select/index";

const DailyView = ({ forecasts, selectedDate, onEventClick, onDateChange }) => {
  const theme = useContext(ThemeContext);
  const isDark = theme[0] === "dark";
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 790;
  const isButtonDesktop = typeof window !== 'undefined' && window.innerWidth >= 1150;
  const [selectedTimeframes, setSelectedTimeframes] = useState({});
  const [selectedScreenshotTypes, setSelectedScreenshotTypes] = useState({}); // 'main' or 'dxy'
  const [gridColumns, setGridColumns] = useState(3);
  const [draggedItem, setDraggedItem] = useState(null);
  const [orderedForecasts, setOrderedForecasts] = useState([]);
  const [forecastTypeFilter, setForecastTypeFilter] = useState("All");

  // Helper function to check if pair is USD-related
  const isUSDRelatedPair = (pairName) => {
    if (!pairName) return false;
    const usdPairs = ['AUDUSD', 'GBPUSD', 'EURUSD', 'NZDUSD', 'USDCHF', 'USDJPY', 'USDCAD', 'XAUUSD', 'XAGUSD', 'BTCUSD', 'SPX500'];
    return usdPairs.includes(pairName);
  };

  // Load saved order on mount
  useEffect(() => {
    const savedOrder = localStorage.getItem('daily-view-forecast-order');
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder);
        setOrderedForecasts(order);
      } catch (e) {
        console.error('Failed to parse saved order:', e);
      }
    }

    // Load saved grid columns preference
    const savedGridColumns = localStorage.getItem('daily-view-grid-columns');
    if (savedGridColumns) {
      try {
        setGridColumns(parseInt(savedGridColumns));
      } catch (e) {
        console.error('Failed to parse saved grid columns:', e);
      }
    }
  }, []);

  // Save order when it changes
  useEffect(() => {
    if (orderedForecasts.length > 0) {
      localStorage.setItem('daily-view-forecast-order', JSON.stringify(orderedForecasts));
    }
  }, [orderedForecasts]);

  // Save grid columns preference when it changes
  useEffect(() => {
    localStorage.setItem('daily-view-grid-columns', gridColumns.toString());
  }, [gridColumns]);

  // Filter forecasts for the selected date and apply saved order
  const daysForecasts = forecasts.filter(f => {
    const forecastDate = moment(f.date).startOf('day');
    const selectedDateMoment = moment(selectedDate).startOf('day');
    return forecastDate.isSame(selectedDateMoment);
  });

  // Apply saved order to forecasts
  const getOrderedForecasts = () => {
    if (orderedForecasts.length === 0) {
      return daysForecasts;
    }

    const ordered = [];
    const remaining = [...daysForecasts];

    // Add forecasts in saved order
    orderedForecasts.forEach(id => {
      const index = remaining.findIndex(f => f._id === id);
      if (index !== -1) {
        ordered.push(remaining[index]);
        remaining.splice(index, 1);
      }
    });

    // Add any new forecasts that weren't in the saved order
    ordered.push(...remaining);

    return ordered;
  };

  const displayForecasts = getOrderedForecasts().filter(forecast =>
    forecastTypeFilter === "All" || forecast.forecastType === forecastTypeFilter
  );

  // Timeframe priority order for fallback - Daily first, then 4h, then 1h, then whatever is available
  const timeframePriority = ["Daily", "4h", "1h", "15m", "30m", "2h", "1m", "5m", "Weekly", "Monthly"];

  // Get the available screenshot for a forecast based on priority
  const getScreenshotByPriority = (forecast, preferredTimeframe, screenshotType = 'main') => {
    const sections = forecast.sections || [];
    
    // If preferred timeframe is available in sections, use it
    const preferredSection = sections.find(s => 
      s.timeframeName?.toLowerCase() === preferredTimeframe.toLowerCase() && 
      (screenshotType === 'dxy' ? s.dxyScreenshotLink : s.screenshotLink)
    );
    if (preferredSection) {
      return { 
        section: {
          ...preferredSection,
          screenshotLink: screenshotType === 'dxy' ? preferredSection.dxyScreenshotLink : preferredSection.screenshotLink
        }, 
        timeframe: preferredTimeframe,
        isDxy: screenshotType === 'dxy'
      };
    }

    // Check if this is old data structure (no sections or empty sections)
    const hasSections = sections.length > 0 && sections.some(s => s.screenshotLink);
    
    if (!hasSections) {
      // Handle old data structure based on preferred timeframe
      if (preferredTimeframe?.toLowerCase() === "1h" && forecast.hourlyLink) {
        return { section: { screenshotLink: forecast.hourlyLink, description: forecast.hourlyDescrtpiton }, timeframe: "1h" };
      }
      if (preferredTimeframe?.toLowerCase() === "daily" && forecast.dailyLink) {
        return { section: { screenshotLink: forecast.dailyLink, description: forecast.dailyDescrtpiton }, timeframe: "Daily" };
      }
      // Fallback to dailyLink if preferred not available
      if (forecast.dailyLink) {
        return { section: { screenshotLink: forecast.dailyLink, description: forecast.dailyDescrtpiton }, timeframe: "Daily" };
      }
      if (forecast.hourlyLink) {
        return { section: { screenshotLink: forecast.hourlyLink, description: forecast.hourlyDescrtpiton }, timeframe: "1h" };
      }
    }

    // Otherwise, fallback to priority order from sections
    for (const timeframe of timeframePriority) {
      const section = sections.find(s => 
        s.timeframeName?.toLowerCase() === timeframe.toLowerCase() && 
        (screenshotType === 'dxy' ? s.dxyScreenshotLink : s.screenshotLink)
      );
      if (section) {
        return { 
          section: {
            ...section,
            screenshotLink: screenshotType === 'dxy' ? section.dxyScreenshotLink : section.screenshotLink
          }, 
          timeframe,
          isDxy: screenshotType === 'dxy'
        };
      }
    }

    return null;
  };

  // Get all available timeframes for a forecast, sorted from lowest to highest
  const getAvailableTimeframes = (forecast) => {
    const sections = forecast.sections || [];
    const timeframes = sections
      .filter(s => s.screenshotLink)
      .map(s => s.timeframeName);

    // Fallback to old data structure
    if (timeframes.length === 0) {
      if (forecast.dailyLink) timeframes.push("Daily");
      if (forecast.hourlyLink) timeframes.push("1h");
    }

    // Sort timeframes from lowest to highest
    const timeframeOrder = {
      '1m': 1,
      '5m': 2,
      '15m': 3,
      '30m': 4,
      '1h': 5,
      '2h': 6,
      '4h': 7,
      'Daily': 8,
      'Weekly': 9,
      'Monthly': 10
    };

    return timeframes.sort((a, b) => {
      const orderA = timeframeOrder[a] || 999;
      const orderB = timeframeOrder[b] || 999;
      return orderA - orderB;
    });
  };

  // Get the default timeframe for a forecast based on priority: Daily -> 4h -> 1h -> whatever is available
  const getDefaultTimeframe = (forecast) => {
    const availableTimeframes = getAvailableTimeframes(forecast);
    
    // Check priority order: Daily, 4h, 1h, then whatever is available
    for (const timeframe of timeframePriority) {
      if (availableTimeframes.some(t => t.toLowerCase() === timeframe.toLowerCase())) {
        return timeframe;
      }
    }
    
    // If none of the priority timeframes are available, return the first available one
    return availableTimeframes.length > 0 ? availableTimeframes[0] : "1h";
  };

  // Handle timeframe selection for a specific forecast
  const handleTimeframeChange = (forecastId, timeframe) => {
    setSelectedTimeframes(prev => ({
      ...prev,
      [forecastId]: timeframe
    }));
  };

  // Handle screenshot type selection for a specific forecast
  const handleScreenshotTypeChange = (forecastId, type) => {
    setSelectedScreenshotTypes(prev => ({
      ...prev,
      [forecastId]: type
    }));
  };

  // Handle drag start
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) return;

    const newOrder = [...displayForecasts];
    const [draggedForecast] = newOrder.splice(draggedItem, 1);
    newOrder.splice(dropIndex, 0, draggedForecast);

    const newOrderIds = newOrder.map(f => f._id);
    setOrderedForecasts(newOrderIds);
    setDraggedItem(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  return (
    <div
      style={{
        padding: isDesktop ? "16px" : "0 16px 16px 16px",
        backgroundColor: isDark ? "#1a1b1e" : "#f9fafb",
        minHeight: "200px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          justifyContent: "space-between",
          alignItems: isDesktop ? "center" : "stretch",
          marginBottom: isDesktop ? "16px" : "24px",
          gap: isDesktop ? "0" : "8px",
        }}
      >
        {/* Mobile: Date title above buttons */}
        {!isDesktop && (
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: isDark ? "#e5e7eb" : "#374151",
              textAlign: "center",
            }}
          >
            {moment(selectedDate).format("dddd, MMMM D")}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isDesktop ? "12px" : "8px",
            justifyContent: isDesktop ? "flex-start" : "center",
            width: isDesktop ? "auto" : "100%",
          }}
        >
          <button
            onClick={() => onDateChange && onDateChange(moment(selectedDate).subtract(1, 'day'))}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#3d3e43" : "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#2d2e33" : "#e5e7eb";
            }}
            style={{
              padding: isDesktop ? "6px 12px" : "4px 8px",
              backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
              color: isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: isDesktop ? "12px" : "11px",
              transition: "all 0.2s ease",
              ...(isDesktop ? {} : { flex: 1 }),
            }}
          >
            Previous
          </button>
          <button
            onClick={() => onDateChange && onDateChange(moment())}
            onMouseEnter={(e) => {
              if (moment().isSame(selectedDate, "day")) {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(55, 136, 216, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "none";
            }}
            style={{
              padding: isDesktop ? "6px 12px" : "4px 8px",
              backgroundColor: moment().isSame(selectedDate, "day")
                ? isDark ? "rgba(55, 136, 216, 0.2)" : "rgba(55, 136, 216, 0.15)"
                : isDark ? "#2d2e33" : "#e5e7eb",
              color: moment().isSame(selectedDate, "day") ? "#3788d8" : isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderColor: moment().isSame(selectedDate, "day") ? "#3788d8" : "transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: isDesktop ? "12px" : "11px",
              transition: "all 0.2s ease",
              ...(isDesktop ? {} : { flex: 1 }),
            }}
          >
            Today
          </button>
          <button
            onClick={() => onDateChange && onDateChange(moment(selectedDate).add(1, 'day'))}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#3d3e43" : "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#2d2e33" : "#e5e7eb";
            }}
            style={{
              padding: isDesktop ? "6px 12px" : "4px 8px",
              backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
              color: isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: isDesktop ? "12px" : "11px",
              transition: "all 0.2s ease",
              ...(isDesktop ? {} : { flex: 1 }),
            }}
          >
            Next
          </button>
        </div>

        {/* Desktop: Date title on the right */}
        {isDesktop && (
          <div
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: isDark ? "#e5e7eb" : "#374151",
            }}
          >
            {moment(selectedDate).format("dddd, MMMM D")}
          </div>
        )}

        {/* Grid Size Radio Buttons - Desktop Only */}
        {isDesktop && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              backgroundColor: isDark ? "#1e1f23" : "#ffffff",
              padding: "6px 12px",
              borderRadius: "8px",
              border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
              marginBottom: "21px",
            }}
          >
            {isButtonDesktop ? [2, 3].map((cols) => (
              <label
                key={cols}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDark ? "#9ca3af" : "#6b7280",
                }}
              >
                <input
                  type="radio"
                  name="grid-columns"
                  value={cols}
                  checked={gridColumns === cols}
                  onChange={() => setGridColumns(cols)}
                  style={{
                    cursor: "pointer",
                    width: "18px",
                    height: "18px",
                  }}
                />
                {cols}
              </label>
            )) : [1, 2].map((cols) => (
              <label
                key={cols}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: isDark ? "#9ca3af" : "#6b7280",
                }}
              >
                <input
                  type="radio"
                  name="grid-columns"
                  value={cols}
                  checked={gridColumns === cols}
                  onChange={() => setGridColumns(cols)}
                  style={{
                    cursor: "pointer",
                    width: "18px",
                    height: "18px",
                  }}
                />
                {cols}
              </label>
            ))}

            {/* Forecast Type Filter */}
            <CustomSelect
              size="small"
              value={forecastTypeFilter}
              onChange={(e) => setForecastTypeFilter(e.target.value)}
              menuItemArray={JSON.stringify([
                { value: "All", label: "All Types" },
                { value: "Main", label: "Main" },
                { value: "Additional", label: "Additional" },
                { value: "Potential", label: "Potential" },
              ])}
              placeholder="All Types"
              sx={{ width: "120px", height: "32px" }}
              variant="compact"
            />
          </div>
        )}
      </div>

      {displayForecasts.length === 0 ? (
        <div
          style={{
            padding: "48px 32px",
            textAlign: "center",
            backgroundColor: isDark ? "#1e1f23" : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
            margin: "16px 0",
          }}
        >
          <div
            style={{
              color: isDark ? "#e5e7eb" : "#6b7280",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            No forecasts for {moment(selectedDate).format("MMMM D, YYYY")}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isDesktop
              ? `repeat(${gridColumns}, 1fr)`
              : "repeat(auto-fill, minmax(350px, 1fr))",
            gap: "16px",
          }}
        >
        {displayForecasts.map((forecast, index) => {
          const defaultTimeframe = getDefaultTimeframe(forecast);
          const selectedTimeframe = selectedTimeframes[forecast._id] || defaultTimeframe;
          const selectedScreenshotType = selectedScreenshotTypes[forecast._id] || 'main';
          const screenshotData = getScreenshotByPriority(forecast, selectedTimeframe, selectedScreenshotType);
          const availableTimeframes = getAvailableTimeframes(forecast);
          
          // Check if DXY screenshot is available for current timeframe
          const currentSection = forecast.sections?.find(s => 
            s.timeframeName?.toLowerCase() === selectedTimeframe.toLowerCase()
          );
          const hasDxyScreenshot = isUSDRelatedPair(forecast.pair) && currentSection?.dxyScreenshotLink;

          return (
            <div
              key={forecast._id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                backgroundColor: isDark ? "#1e1f23" : "#ffffff",
                border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                borderRadius: "12px",
                overflow: "hidden",
                cursor: "grab",
                transition: "all 0.2s ease",
                opacity: draggedItem === index ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (draggedItem === null) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = isDark
                    ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                    : "0 4px 12px rgba(0, 0, 0, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (draggedItem === null) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.cursor = "grabbing";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.cursor = "grab";
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* Left side: Pair name and direction */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          padding: "4px 12px",
                          borderRadius: "8px",
                          backgroundColor:
                            forecast.direction === "Short"
                              ? isDark
                                ? "rgba(219, 6, 113, 0.15)"
                                : "rgba(219, 6, 113, 0.1)"
                              : forecast.direction === "Long"
                                ? isDark
                                  ? "rgba(38, 166, 93, 0.15)"
                                  : "rgba(38, 166, 93, 0.1)"
                                : isDark
                                  ? "rgba(237, 158, 14, 0.15)"
                                  : "rgba(237, 158, 14, 0.1)",
                          border: `2px solid ${
                            forecast.direction === "Short"
                              ? "#db0671"
                              : forecast.direction === "Long"
                                ? "#26a65d"
                                : "#ED9E0E"
                          }`,
                          color:
                            forecast.direction === "Short"
                              ? "#db0671"
                              : forecast.direction === "Long"
                                ? "#26a65d"
                                : "#ED9E0E",
                          fontSize: gridColumns === 2 ? "18px" : "16px",
                          fontWeight: "700",
                          display: "flex",
                          alignItems: "center",
                 
                          gap: "6px",
                        }}
                      >
                        {forecast.pair}
                        {forecast.forecastType && (
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              backgroundColor:
                                forecast.forecastType === "Main"
                                  ? isDark
                                    ? "rgba(38, 166, 93, 0.4)"
                                    : "rgba(38, 166, 93, 0.3)"
                                  : forecast.forecastType === "Additional"
                                    ? isDark
                                      ? "rgba(237, 158, 14, 0.4)"
                                      : "rgba(237, 158, 14, 0.3)"
                                    : isDark
                                      ? "rgba(66, 153, 225, 0.4)"
                                      : "rgba(66, 153, 225, 0.3)",
                              border: `1.5px solid ${
                                forecast.forecastType === "Main"
                                  ? "#26a65d"
                                  : forecast.forecastType === "Additional"
                                    ? "#ED9E0E"
                                    : "#4299e1"
                              }`,
                              color: isDark ? "#ffffff" : "#000000",
                              fontSize: gridColumns === 2 ? "11px" : "10px",
                              fontWeight: "700",
                              textTransform: "uppercase",
                            }}
                          >
                            {forecast.forecastType.charAt(0)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side: Timeframe Radio Buttons */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
                    {availableTimeframes.length > 1 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                          backgroundColor: isDark ? "#1e1f23" : "#ffffff",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                        }}
                      >
                        {availableTimeframes.map((timeframe) => (
                          <label
                            key={timeframe}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: selectedTimeframe === timeframe 
                                ? isDark ? "#3788d8" : "#2563eb"
                                : isDark ? "#9ca3af" : "#6b7280",
                              transition: "color 0.2s ease",
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={() => handleTimeframeChange(forecast._id, timeframe)}
                          >
                            <input
                              type="radio"
                              name={`timeframe-${forecast._id}`}
                              value={timeframe}
                              checked={selectedTimeframe === timeframe}
                              onChange={() => handleTimeframeChange(forecast._id, timeframe)}
                              style={{
                                cursor: "pointer",
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            {timeframe}
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Screenshot Type Radio Buttons - Only for USD pairs with DXY screenshot */}
                    {hasDxyScreenshot && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                          backgroundColor: isDark ? "#1e1f23" : "#ffffff",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: selectedScreenshotType === 'main'
                              ? isDark ? "#3788d8" : "#2563eb"
                              : isDark ? "#9ca3af" : "#6b7280",
                            transition: "color 0.2s ease",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name={`screenshot-type-${forecast._id}`}
                            value="main"
                            checked={selectedScreenshotType === 'main'}
                            onChange={() => handleScreenshotTypeChange(forecast._id, 'main')}
                            style={{
                              cursor: "pointer",
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          {forecast.pair}
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: selectedScreenshotType === 'dxy'
                              ? "#ED9E0E"
                              : isDark ? "#9ca3af" : "#6b7280",
                            transition: "color 0.2s ease",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name={`screenshot-type-${forecast._id}`}
                            value="dxy"
                            checked={selectedScreenshotType === 'dxy'}
                            onChange={() => handleScreenshotTypeChange(forecast._id, 'dxy')}
                            style={{
                              cursor: "pointer",
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          DXY
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Thumbnail */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  backgroundColor: isDark ? "#2d2e33" : "#f3f4f6",
                  overflow: "hidden",
                }}
              >
                {screenshotData ? (
                  <img
                    src={screenshotData.section.screenshotLink}
                    alt={`${forecast.pair} ${screenshotData.timeframe} chart`}
                    onClick={() => onEventClick && onEventClick({
                      title: `${forecast.pair} Forecast`,
                      publicId: forecast._id,
                      category: "forecast"
                    })}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: isDark ? "#6b7280" : "#9ca3af",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    No screenshot available
                  </div>
                )}
              </div>

              {/* Meta Info Badges */}
              <div
                style={{
                  padding: "12px 16px",
                  display: "flex",
                  flexWrap: "wrap",
                  width: '65%',
                  margin: '0 auto',
                  justifyContent: "space-around",
                }}
              >
                {forecast.positioning && (
                  <div
                    style={{
                      padding: gridColumns === 2 ? "6px 12px" : "4px 8px",
                      borderRadius: "16px",
                      backgroundColor:
                        forecast.positioning === "Excellent Positioning"
                          ? isDark
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : isDark
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)",
                      border: `2px solid ${
                        forecast.positioning === "Excellent Positioning"
                          ? "#26a65d"
                          : "#ED9E0E"
                      }`,
                      minWidth: gridColumns === 2 ? "80px" : "60px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "8px" : "7px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: isDark ? "#9ca3af" : "#6b7280",
                        margin: "0 0 1px 0",
                      }}
                    >
                      POSITIONING
                    </p>
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "12px" : "10px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          forecast.positioning === "Excellent Positioning"
                            ? "#26a65d"
                            : "#ED9E0E",
                      }}
                    >
                      {forecast.positioning.replace(" Positioning", "").toUpperCase()}
                    </p>
                  </div>
                )}
                {forecast.sequence && (
                  <div
                    style={{
                      padding: gridColumns === 2 ? "6px 12px" : "4px 8px",
                      borderRadius: "16px",
                      backgroundColor:
                        forecast.sequence === "Excellent Sequence"
                          ? isDark
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : isDark
                            ? "rgba(237, 158, 14, 0.15)"
                            : "rgba(237, 158, 14, 0.1)",
                      border: `2px solid ${
                        forecast.sequence === "Excellent Sequence"
                          ? "#26a65d"
                          : "#ED9E0E"
                      }`,
                      minWidth: gridColumns === 2 ? "80px" : "60px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "8px" : "7px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: isDark ? "#9ca3af" : "#6b7280",
                        margin: "0 0 1px 0",
                      }}
                    >
                      SEQUENCE
                    </p>
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "12px" : "10px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          forecast.sequence === "Excellent Sequence"
                            ? "#26a65d"
                            : "#ED9E0E",
                      }}
                    >
                      {forecast.sequence.replace(" Sequence", "").toUpperCase()}
                    </p>
                  </div>
                )}
                {forecast.grade && (
                  <div
                    style={{
                      padding: gridColumns === 2 ? "6px 12px" : "4px 8px",
                      borderRadius: "16px",
                      backgroundColor:
                        forecast.grade === "Low Risk"
                          ? isDark
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : isDark
                            ? "rgba(242, 54, 69, 0.15)"
                            : "rgba(242, 54, 69, 0.1)",
                      border: `2px solid ${
                        forecast.grade === "Low Risk" ? "#26a65d" : "#f23645"
                      }`,
                      minWidth: gridColumns === 2 ? "80px" : "60px",
                      textAlign: "center",
                    }}
                  >
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "8px" : "7px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: isDark ? "#9ca3af" : "#6b7280",
                        margin: "0 0 1px 0",
                      }}
                    >
                      RISK
                    </p>
                    <p
                      style={{
                        fontSize: gridColumns === 2 ? "12px" : "10px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color: forecast.grade === "Low Risk" ? "#26a65d" : "#f23645",
                      }}
                    >
                      {forecast.grade.replace(" Risk", "").toUpperCase()}
                    </p>
                  </div>
                )}
              </div>

              {/* Forecast Details */}
              <div
                style={{
                  padding: "12px 16px",
                }}
              >
                {/* Timeframe Description/Note */}
                {screenshotData?.section?.description ? (
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#9ca3af" : "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    {screenshotData.section.description}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#6b7280" : "#9ca3af",
                      fontStyle: "italic",
                      lineHeight: "1.5",
                    }}
                  >
                    No notes for this timeframe
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {displayForecasts.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "24px 16px",
            color: isDark ? "#6b7280" : "#9ca3af",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          End of forecasts
        </div>
      )}
    </div>
  );
};

export default DailyView;
